import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Users, GraduationCap, BookOpen, DollarSign, AlertCircle, TrendingUp, FileSpreadsheet, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      if (err.response?.status === 401) {
        setError("Your session has expired or you are not authorized. Please sign in again.");
      } else {
        setError("We could not load the dashboard statistics right now.");
      }
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(Array.isArray(res.data) ? res.data : []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!token) {
      setError("Please sign in to view the dashboard.");
      setLoading(false);
      setStats(null);
      return;
    }

    fetchStats();
    fetchAnnouncements();
  }, [authLoading, fetchAnnouncements, fetchStats, token]);

  const money = (value) => `USD ${Number(value || 0).toFixed(2)}`;
  const totalIncome = Number(stats?.total_income_received ?? stats?.total_collected ?? 0);
  const totalExpenses = Number(stats?.total_expenses ?? 0);
  const expectedIncome = Number(stats?.expected_income ?? stats?.expectedFees ?? 0);
  const expectedExpenses = Number(stats?.expected_expenses ?? stats?.expectedExpenses ?? 0);
  const currentBalance = Number(stats?.current_balance ?? stats?.currentBalance ?? 0);
  const expectedClosingBalance = Number(stats?.expected_closing_balance ?? stats?.expectedBalance ?? 0);

  const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <Card className="border-slate-200 card-hover">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-heading font-bold text-slate-900 mt-1">
              {loading ? "-" : value}
            </p>
            {subtext && (
              <p className="text-xs text-slate-400 mt-1">{subtext}</p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="text-white" size={20} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">
          Welcome back, {user?.username}!
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Here's what's happening at Cresabel today.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700" role="alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {user?.role === "admin" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Students"
              value={stats?.total_students || 0}
              icon={GraduationCap}
              color="bg-pink-500"
            />
            <StatCard
              title="Total Teachers"
              value={stats?.total_teachers || 0}
              icon={Users}
              color="bg-slate-700"
            />
            <StatCard
              title="Total Classes"
              value={stats?.total_classes || 0}
              icon={BookOpen}
              color="bg-pink-400"
            />
            <StatCard
              title="Income Received"
              value={money(totalIncome)}
              icon={DollarSign}
              color="bg-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Expected Income"
              value={money(expectedIncome)}
              icon={DollarSign}
              color="bg-blue-500"
            />
            <StatCard
              title="Income Received"
              value={money(totalIncome)}
              icon={TrendingUp}
              color="bg-emerald-500"
            />
            <StatCard
              title="Expected Expenses"
              value={money(expectedExpenses)}
              icon={AlertCircle}
              color="bg-amber-500"
            />
            <StatCard
              title="Current Balance"
              value={money(currentBalance)}
              icon={DollarSign}
              color="bg-slate-700"
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Expenses"
              value={money(totalExpenses)}
              icon={AlertCircle}
              color="bg-red-500"
            />
            <StatCard
              title="Expected Closing"
              value={money(expectedClosingBalance)}
              icon={TrendingUp}
              color="bg-blue-500"
            />
            <StatCard
              title="Fees Collected"
              value={money(stats?.total_fees_collected)}
              icon={DollarSign}
              color="bg-emerald-600"
            />
            <StatCard
              title="Net Balance"
              value={money(currentBalance)}
              icon={BookOpen}
              color="bg-slate-700"
            />
          </div>

          {/* Fee Status Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="border-slate-200 border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Income Received</p>
                    <p className="text-xl font-bold text-emerald-600">{money(totalIncome)}</p>
                  </div>
                  <div className="text-emerald-500 text-xs font-medium">TOTAL</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expected Income</p>
                    <p className="text-xl font-bold text-amber-600">{money(expectedIncome)}</p>
                  </div>
                  <div className="text-amber-500 text-xs font-medium">EXPECT</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expected Expenses</p>
                    <p className="text-xl font-bold text-red-600">{money(expectedExpenses)}</p>
                  </div>
                  <div className="text-red-500 text-xs font-medium">COST</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Teacher Dashboard */}
      {user?.role === "teacher" && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <StatCard
              title="Students"
              value={stats?.total_students || 0}
              icon={GraduationCap}
              color="bg-pink-500"
            />
            <StatCard
              title="Results Entered"
              value={stats?.total_results || 0}
              icon={FileSpreadsheet}
              color="bg-blue-500"
            />
          </div>
        </>
      )}

      {/* Announcements */}
      {Array.isArray(announcements) && announcements.length > 0 && (
        <div className="mb-6 space-y-3">
          {announcements.map((a) => (
            <Card key={a.id} className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 flex items-start gap-3">
                <Megaphone className="text-blue-500 mt-0.5 shrink-0" size={20} />
                <div>
                  <p className="font-semibold text-blue-900">{a.title}</p>
                  <p className="text-sm text-blue-700">{a.message}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Role-specific Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-slate-900">
              Your Role: {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {user?.role === "admin" && (
                <>
                  <p className="text-sm text-slate-600">As an admin, you can:</p>
                  <ul className="list-disc list-inside text-slate-500 space-y-1 text-sm">
                    <li>Create other admin accounts</li>
                    <li>Manage teachers and view their personal info</li>
                    <li>Add, edit, and remove students</li>
                    <li>Bulk import students via Excel/CSV</li>
                    <li>Manage classes and fee records</li>
                    <li>View activity logs</li>
                  </ul>
                </>
              )}
              {user?.role === "teacher" && (
                <>
                  <p className="text-sm text-slate-600">As a teacher, you can:</p>
                  <ul className="list-disc list-inside text-slate-500 space-y-1 text-sm">
                    <li>View student information</li>
                    <li>View all classes</li>
                    <li>Enter and view student results</li>
                    <li>Change your password</li>
                  </ul>
                </>
              )}
              {user?.role === "student" && (
                <>
                  <p className="text-sm text-slate-600">As a student, you can:</p>
                  <ul className="list-disc list-inside text-slate-500 space-y-1 text-sm">
                    <li>View your academic results (if fees are paid)</li>
                    <li>Check your profile information</li>
                    <li>Change your password</li>
                  </ul>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Important Notice */}
        <Card className="border-slate-200 border-l-4 border-l-pink-500">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-slate-900 flex items-center gap-2">
              <AlertCircle size={18} className="text-pink-500" />
              Important Notice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-slate-600">
              <p>
                Welcome to Cresabel Student Portal! This platform helps manage:
              </p>
              <ul className="list-disc list-inside text-slate-500 space-y-1">
                <li>Student enrollment and records</li>
                <li>Teacher directory management</li>
                <li>Academic results tracking</li>
                <li>Fee status monitoring</li>
              </ul>
              {user?.role === "student" && (
                <div className="mt-3 p-2 bg-pink-50 rounded-lg border border-pink-100">
                  <p className="text-xs text-pink-700">
                    <strong>Note:</strong> You can only view your results after your fees have been marked as paid.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
